import { ApolloClient, gql } from '@apollo/client';
import { TTranslator } from '@smartshell/ui-kit';

import { TLocalesKeys } from 'localization';
import notification from 'services/notificationService';
import {
  ISeance,
  IServerTariff,
  TClient,
  THostStatus,
  TSeanceStatusType,
} from 'types';

import { getCosts } from './tariff';

/**
 * host status getter, returns one of "FREE" | "BUSY" | "IN_SERVICE"
 * @param host
 * @return {"BUSY" | "FREE" | "IN_SERVICE"}
 */
export const getHostStatus = ({
  in_service,
  client_sessions,
}: {
  in_service: boolean;
  client_sessions?: Array<{ id: number } | undefined>;
}): THostStatus => {
  if (in_service) {
    return 'IN_SERVICE';
  }

  return client_sessions?.length ? 'BUSY' : 'FREE';
};

/**
 * returns seance with active status
 * @param {Array<ISeance>} seances
 * @return {ISeance | null}
 */
export const getActiveSeance = <T extends Pick<ISeance, 'status'>>(
  seances: Array<T>
): T | null => seances.find((seance) => seance.status === 'ACTIVE') ?? null;

/**
 * TODO: Удалить когда Саня сделает нормальное решение на сервере
 * Функция временная, используется как заглушка для расчета примерной длительности поминутного тарифа
 * @param groupId ID группы, в которой находится ПК
 * @param priceList Список костов текущего тарифа
 * @param client Пользователь за текущим компом
 * @param shouldUseClientDiscount Должна ли использоваться клиентская скидка
 */
export const getPerMinuteTariffRest = (
  groupId: number,
  priceList: IServerTariff['price_list'],
  client: TClient | null,
  shouldUseClientDiscount: boolean
): number | null => {
  if (!client?.deposit) return null;

  const currentGroup = priceList.find((item) =>
    item.host_groups?.some((group) => Number(group.id) === groupId)
  );

  if (!currentGroup) return null;

  const costsMap = getCosts(currentGroup.cost_map);

  const { deposit, user_discount } = client;
  const discount = shouldUseClientDiscount && user_discount ? user_discount : 0;
  const currentCostMultiplier = discount ? 1 - discount / 100 : 1;

  const currentCost =
    (costsMap.user_cost ?? costsMap.cost) * currentCostMultiplier;

  return Math.floor(deposit / currentCost) * 60;
};

type HostForProcessChanges = {
  id: number;
  group_id: number;
  alias: string;
  online: boolean;
  client_sessions: Array<{ id: number } | undefined>;
};
/**
 * Обрабатывает изменение состояний хостов:
 * 1) если статус хоста сменился на "Выключен" - выбрасывает нотификацию
 * 2) если на хосте завершился сеанс - выбрасывает нотификацию
 * @param newHosts новые хосты
 * @param previousHosts старые хосты
 * @param t translator
 */
export const processHostsChanges = (
  newHosts: Array<HostForProcessChanges>,
  previousHosts: Array<HostForProcessChanges>,
  t: TTranslator<TLocalesKeys>
): void => {
  newHosts.forEach((newHost) => {
    const previousHost = previousHosts.find(
      (prevHost) =>
        newHost.id === prevHost.id && newHost.group_id === prevHost.group_id
    );

    if (previousHost) {
      if (previousHost.online && !newHost.online) {
        notification.info({
          t,
          title: t('host.notification.lostConnection'),
          leftDescription: newHost.alias,
          type: 'warning',
          dismiss: 3,
        });
      }

      const previousSession = previousHost.client_sessions[0];

      if (
        previousSession?.id &&
        !newHosts.some(
          (host) => host.client_sessions[0]?.id === previousSession.id
        )
      ) {
        notification.info({
          t,
          type: 'info',
          title: t('host.notification.seanceFinished'),
          leftDescription: newHost.alias,
          dismiss: 3,
        });
      }
    }
  });
};

/**
 * Возвращает логин клиента за хостом
 * 1) если есть активная клиентская сессия возвращает логин этого клиента или "Гость"
 * 2) если есть активная хостовая сессия возвращает логин клиента из неё
 * 3) в противном случае возвращает null
 * @param {THost} host текущий хост
 * @param t функция переводчик
 */
export const getClientFromHostLogin = (
  host: {
    client_sessions: Array<
      { client: { nickname: string; phone: string } | null } | undefined
    >;
    sessions: Array<{ user: { nickname: string } | null } | undefined>;
  },
  t: TTranslator<TLocalesKeys>
): string | null => {
  const activeClientSession = host.client_sessions[0];
  const hostSession = host.sessions[0];

  if (activeClientSession) {
    return (
      activeClientSession.client?.nickname ??
      activeClientSession.client?.phone ??
      t('client.guest')
    );
  }

  return hostSession?.user?.nickname ?? hostSession?.user?.nickname ?? null;
};

interface IGetRemainTimeArgs {
  apolloClient: ApolloClient<unknown>;
  activeSession: {
    client: {
      deposit: number;
      user_discount: number;
    } | null;
    seances: Array<{
      status: TSeanceStatusType;
      tariff: {
        per_minute?: boolean;
        id: number;
      };
    }>;
    duration: number;
    elapsed: number;
  };
  shouldUseClientDiscount: boolean;
  group_id: number;
}
export const getRemainTime = ({
  apolloClient,
  shouldUseClientDiscount,
  activeSession,
  group_id,
}: IGetRemainTimeArgs): number | null => {
  if (!activeSession) {
    return null;
  }

  const { client, seances, duration, elapsed } = activeSession;

  const tariff = getActiveSeance(seances)?.tariff;

  if (!tariff) {
    return null;
  }

  if (tariff.per_minute) {
    const perMinuteTariffId = apolloClient.cache.identify({
      __typename: 'Tariff',
      id: tariff.id,
    });

    const cachedTariffData = apolloClient.readFragment({
      id: perMinuteTariffId,
      fragment: gql`
        fragment TariffPriceList on Tariff {
          use_global_discounts
          price_list {
            host_groups {
              title
              id
            }
            cost_map {
              title
              value
            }
          }
        }
      `,
    });

    if (!cachedTariffData) return null;

    return getPerMinuteTariffRest(
      group_id,
      cachedTariffData.price_list,
      client,
      shouldUseClientDiscount || cachedTariffData.use_global_discounts
    );
  }

  if (duration - elapsed <= 0) {
    return null;
  }

  return duration - elapsed;
};

export const checkShellVersion = (version?: string): boolean => {
  if (!version) return false;

  return /^\d+\.\d+\.\d+\.\d+$/.test(version);
};
